import { useAuth } from "@/hooks/useAuth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu as MenuIcon, X as CloseIcon, User, Settings, Loader2 } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User as UserType } from "@supabase/supabase-js";

interface NavLinksProps {
  isDataInput: boolean;
  isAdmin: boolean;
  user: UserType | null;
  onMobileClick?: () => void;
}

const NavLinks = ({ isDataInput, isAdmin, user, onMobileClick }: NavLinksProps) => {
  const location = useLocation();
  const activeClass = "bg-primary/20 text-primary";
  const inactiveClass = "text-gray-300 hover:bg-white/20 hover:text-white";

  // If user is admin, show admin navigation
  if (isAdmin) {
    return (
      <>
        <Link 
          to="/admin" 
          className={`block px-4 py-2 text-sm rounded-md font-medium ${
            location.pathname === '/admin' ? activeClass : inactiveClass
          }`}
          onClick={onMobileClick}
        >
          Admin Dashboard
        </Link>
        <Link 
          to="/admin/homepage-editor" 
          className={`block px-4 py-2 text-sm rounded-md font-medium ${
            location.pathname === '/admin/homepage-editor' ? activeClass : inactiveClass
          }`}
          onClick={onMobileClick}
        >
          Homepage Editor
        </Link>
        <Link 
          to="/admin/manage-questions" 
          className={`block px-4 py-2 text-sm rounded-md font-medium ${
            location.pathname === '/admin/manage-questions' ? activeClass : inactiveClass
          }`}
          onClick={onMobileClick}
        >
          Manage Questions
        </Link>
        <Link 
          to="/admin/users" 
          className={`block px-4 py-2 text-sm rounded-md font-medium ${
            location.pathname === '/admin/users' ? activeClass : inactiveClass
          }`}
          onClick={onMobileClick}
        >
          User Management
        </Link>
        <Link 
          to="/admin/settings" 
          className={`block px-4 py-2 text-sm rounded-md font-medium ${
            location.pathname === '/admin/settings' ? activeClass : inactiveClass
          }`}
          onClick={onMobileClick}
        >
          Settings
        </Link>
      </>
    );
  }

  // Default navigation for non-admin users
  return (
    <>
      <Link 
        to="/dashboard" 
        className={`block px-4 py-2 text-sm rounded-md font-medium ${
          location.pathname === '/dashboard' ? activeClass : inactiveClass
        }`}
        onClick={onMobileClick}
      >
        Dashboard
      </Link>
      <Link 
        to="/brain-training" 
        className={`block px-4 py-2 text-sm rounded-md font-medium ${
          location.pathname === '/brain-training' ? activeClass : inactiveClass
        }`}
        onClick={onMobileClick}
      >
        Brain Training
      </Link>
      <Link 
        to="/practice" 
        className={`block px-4 py-2 text-sm rounded-md font-medium ${
          location.pathname === '/practice' ? activeClass : inactiveClass
        }`}
        onClick={onMobileClick}
      >
        Practice
      </Link>
      <Link 
        to="/profile" 
        className={`block px-4 py-2 text-sm rounded-md font-medium ${
          location.pathname === '/profile' ? activeClass : inactiveClass
        }`}
        onClick={onMobileClick}
      >
        Profile
      </Link>
    </>
  );
};

export default function Navigation() {
  const { user, isDataInput, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (loading) {
    return (
      <nav className="bg-[#1EAEDB]">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-5 w-5 animate-spin text-white" />
              <span className="text-white">Loading...</span>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-[#1EAEDB]">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white"
            >
              {isMobileMenuOpen ? (
                <CloseIcon className="h-5 w-5" />
              ) : (
                <MenuIcon className="h-5 w-5" />
              )}
            </Button>
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/lovable-uploads/fa3e7201-848d-4ab9-a19d-74319434852e.png" 
                alt="IQify Logo" 
                className="h-12 md:h-14"
                onError={(e) => {
                  e.currentTarget.src = '/logo.png';
                }}
              />
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <NavLinks 
              isDataInput={isDataInput} 
              isAdmin={isAdmin} 
              user={user}
            />
          </div>

          <div className="flex items-center space-x-4">
            <Avatar>
              <AvatarImage src={user?.user_metadata?.avatar_url} alt="Profile" />
              <AvatarFallback>{user?.user_metadata?.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/auth')}
              className="text-white hover:text-blue-100"
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4">
            <NavLinks 
              isDataInput={isDataInput} 
              isAdmin={isAdmin} 
              user={user}
              onMobileClick={() => setMobileMenuOpen(false)}
            />
          </div>
        )}
      </div>
    </nav>
  );
}
